import { useCallback, useMemo, useRef, useState } from "react";
import type { PlateAppearanceResult } from "../../shared/domain/gameReducer";
import type { RunnerDestination } from "../shared/types/contracts";

import { expectedRunsForPlay, validatePlayDraft } from "../domain/playDraft";
import { defaultDecisions } from "../data/viewModel";

export interface PendingPlay {
  r: PlateAppearanceResult;
  decisions: Record<number, RunnerDestination>;
  rbi: number;
}

export interface PendingRbi {
  batterId: string | null;
  runs: number;
}

export interface PlayDraftController {
  pendingPlay: PendingPlay | null;
  setPendingPlay: (play: PendingPlay | null) => void;
  pendingRBI: PendingRbi | null;
  setPendingRBI: (value: PendingRbi | null) => void;
  resetPlayDraft: () => void;
  beginResult: (
    result: PlateAppearanceResult,
    bases: readonly (string | null)[],
  ) => void;
  setDecision: (baseIndex: number, destination: RunnerDestination) => void;
  adjustPendingRbi: (delta: number) => void;
  clearPendingPlay: () => void;
  consumePendingRbi: () => PendingRbi | null;
  resolvePendingRbi: (expected: PendingRbi) => void;
  validation: ReturnType<typeof validatePlayDraft> | null;
}

export function usePlayDraft(): PlayDraftController {
  const [pendingPlay, setPendingPlay] = useState<PendingPlay | null>(null);
  const [pendingRBI, setPendingRBIState] = useState<PendingRbi | null>(null);
  const pendingRbiRef = useRef<PendingRbi | null>(null);

  const setPendingRBI = useCallback((value: PendingRbi | null) => {
    pendingRbiRef.current = value;
    setPendingRBIState(value);
  }, []);
  const resetPlayDraft = useCallback(() => {
    setPendingPlay(null);
    setPendingRBI(null);
  }, [setPendingRBI]);
  const beginResult = useCallback(
    (result: PlateAppearanceResult, bases: readonly (string | null)[]) => {
      const decisions = defaultDecisions(result, bases);
      const expectedRuns = expectedRunsForPlay(result, decisions);
      setPendingPlay({
        r: result,
        decisions,
        rbi: result.error ? 0 : expectedRuns,
      });
    },
    [],
  );
  const setDecision = useCallback(
    (baseIndex: number, destination: RunnerDestination) => {
      setPendingPlay((play) => {
        if (!play) return null;
        const decisions = {
          ...play.decisions,
          [baseIndex]: destination,
        };
        const expectedRuns = expectedRunsForPlay(play.r, decisions);
        return {
          ...play,
          decisions,
          rbi: play.r.error ? 0 : expectedRuns,
        };
      });
    },
    [],
  );
  const adjustPendingRbi = useCallback((delta: number) => {
    setPendingPlay((play) =>
      play
        ? {
            ...play,
            rbi: Math.max(0, play.rbi + delta),
          }
        : null,
    );
  }, []);
  const clearPendingPlay = useCallback(() => setPendingPlay(null), []);
  const consumePendingRbi = useCallback(() => {
    const value = pendingRbiRef.current;
    setPendingRBI(null);
    return value;
  }, [setPendingRBI]);
  const resolvePendingRbi = useCallback(
    (expected: PendingRbi) => {
      const current = pendingRbiRef.current;
      if (
        current?.batterId === expected.batterId &&
        current.runs === expected.runs
      ) {
        setPendingRBI(null);
      }
    },
    [setPendingRBI],
  );
  const validation = useMemo(
    () =>
      pendingPlay
        ? validatePlayDraft(pendingPlay.r, pendingPlay.decisions)
        : null,
    [pendingPlay],
  );

  return {
    pendingPlay,
    setPendingPlay,
    pendingRBI,
    setPendingRBI,
    resetPlayDraft,
    beginResult,
    setDecision,
    adjustPendingRbi,
    clearPendingPlay,
    consumePendingRbi,
    resolvePendingRbi,
    validation,
  };
}
